import {useMutation, useQueryClient} from '@github-ui/react-query'
import {verifiedFetch} from '@github-ui/verified-fetch'
import type {CreateListResponse, StarState} from '../types'
import {parseStarCount} from '../utils/parse-star-count'
import {userListsQueryKey} from './use-user-lists-query'

interface CreateListInput {
  name: string
  description: string
  isPrivate: boolean
  repositoryId?: string
}

interface CreateListResult {
  didStar: boolean
  starCount: number | null
}

async function getCreateListErrorMessage(response: Response): Promise<string> {
  try {
    const payload = (await response.json()) as CreateListResponse
    return payload.error?.trim() || 'Failed to create list'
  } catch {
    return 'Failed to create list'
  }
}

interface UseCreateListMutationOptions {
  createPath: string
  listsApiPath: string
  starCount: number
  onStarStateChange: (next: StarState) => void
  onClose: () => void
}

/**
 * Hook to create a new user star list.
 * Handles the POST request, updates star state when the creation
 * implicitly stars the repository, and invalidates the lists query.
 */
export function useCreateListMutation({
  createPath,
  listsApiPath,
  starCount,
  onStarStateChange,
  onClose,
}: UseCreateListMutationOptions) {
  const queryClient = useQueryClient()

  return useMutation<CreateListResult, Error, CreateListInput>({
    mutationFn: async ({name, description, isPrivate, repositoryId}) => {
      const body = new URLSearchParams()
      body.append('user_list[name]', name)
      body.append('user_list[description]', description)
      body.append('user_list[private]', isPrivate ? '1' : '0')
      if (repositoryId) {
        body.append('repository_id', repositoryId)
      }

      const response = await verifiedFetch(createPath, {
        method: 'POST',
        body,
        headers: {Accept: 'application/json'},
      })

      if (!response.ok) {
        const message = await getCreateListErrorMessage(response)
        throw new Error(message)
      }

      const payload = (await response.json()) as CreateListResponse
      return {
        didStar: payload.didStar === true,
        starCount: parseStarCount(payload.starCount),
      }
    },

    onSuccess: result => {
      if (result.didStar) {
        onStarStateChange({
          starred: true,
          starCount: result.starCount ?? starCount + 1,
        })
      }

      onClose()
    },

    onSettled: () => {
      void queryClient.invalidateQueries({queryKey: userListsQueryKey(listsApiPath)})
    },
  })
}
